import { Checkbox, CheckboxChangeEvent } from 'primereact/checkbox';
import { Dropdown, DropdownChangeEvent } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { SelectButton } from 'primereact/selectbutton';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { capitalizeFirstLetter } from '../../../utils/texts';
import './VotingDataCardsFilter.css';
import { getApiClient, DropdownOptions, DropdownOptionsCode } from '../../../api';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMunicipalityId } from '../../../hooks/useCouncilPath';
import { Button } from 'primereact/button';
import { URLKeys } from '../../../Constants';
import { useLocalizationLabels } from '../../../context/LocalizationLabelContext';

type VotingFormDataType = {
    [URLKeys.voteDescription]: string;
    [URLKeys.meetingNumber]: number | null; // should be number, will be unified in the future
    [URLKeys.memberNumber]: number | null;
    [URLKeys.memberVote]: 'all' | '1' | '2' | number;
    [URLKeys.voteResult]: 'all' | '1' | '2';
    [URLKeys.sortBy]: 'nejnovejsi' | 'nejstarsi';
    [URLKeys.show]: string[]; // replacement for checkboxes (validVotes, nonProceduralVotes, publicVotes)
    [URLKeys.voteDescriptionText]: string | null;
};

type DropdownOptionsType = {
    meeting: DropdownOptions[];
    member: DropdownOptions[];
    otherVoteOptions: DropdownOptionsCode[];
};

const sortingOptionsDict = {
    nejnovejsi: (a: any, b: any) => b.voteId - a.voteId,
    nejstarsi: (a: any, b: any) => a.voteId - b.voteId
};

type Props = {
    setFilterFunc: (filterFunc: () => (a: any, b: any) => number) => void;
};
const VotingDataCardsFilter = ({ setFilterFunc }: Props) => {
    const { t } = useTranslation('votings');

    const { bodyType = '', zastupitelstvoId } = useParams();
    const municipalitaId = useMunicipalityId();
    const [searchParams, setSearchParams] = useSearchParams();
    const [dropdownOptions, setDropdownOptions] = useState<DropdownOptionsType>({
        meeting: [],
        member: [],
        otherVoteOptions: [],
    });

    const {voteOptionLabels} = useLocalizationLabels();

    const sortByOptions = [
        { label: t('VotingDataCardsFilter.latestSort'), value: 'nejnovejsi' as const },
        { label: t('VotingDataCardsFilter.oldestSort'), value: 'nejstarsi' as const }
    ];
    const initialFormState: VotingFormDataType = {
        [URLKeys.voteDescription]: searchParams.get(URLKeys.voteDescription) || '',
        [URLKeys.meetingNumber]: searchParams.get(URLKeys.meetingNumber)
            ? Number(searchParams.get(URLKeys.meetingNumber))
            : null,
        [URLKeys.memberNumber]: searchParams.get(URLKeys.memberNumber)
            ? Number(searchParams.get(URLKeys.memberNumber))
            : null,
        [URLKeys.memberVote]:
            (searchParams.get(URLKeys.memberVote) as VotingFormDataType[(typeof URLKeys)['memberVote']]) || 'all',
        [URLKeys.voteResult]:
            (searchParams.get(URLKeys.voteResult) as VotingFormDataType[typeof URLKeys.voteResult]) || 'all',
        [URLKeys.sortBy]:
            (searchParams.get(URLKeys.sortBy) as VotingFormDataType[(typeof URLKeys)['sortBy']]) || 'nejnovejsi',
        [URLKeys.show]: searchParams.get(URLKeys.show) ? searchParams.get(URLKeys.show)!.split(',') : [],
        [URLKeys.voteDescriptionText]: searchParams.get(URLKeys.voteDescriptionText)
    };

    const [formData, setFormData] = useState<VotingFormDataType>(initialFormState);

    const show = formData[URLKeys.show];
    const sortBy = formData[URLKeys.sortBy];
    const voteDescription = formData[URLKeys.voteDescription];
    const meetingNumber = formData[URLKeys.meetingNumber];
    const memberNumber = formData[URLKeys.memberNumber];
    const memberVote = formData[URLKeys.memberVote];
    const voteResult = formData[URLKeys.voteResult];

    const onInputChange = (
        e: React.ChangeEvent<HTMLInputElement> | DropdownChangeEvent | CheckboxChangeEvent | any
    ) => {
        const { id, value } = e.target;
        let updatedFormData = { ...formData };
        let updatedSearchParams = new URLSearchParams(searchParams);

        const handleInputChange = (id: string, value: any) => {
            updatedFormData = { ...formData, [id]: value };
            updateSearchParams(id, value, value && value !== 'all' && value !== '');

            // Reset memberVote if memberNumber is cleared
            if (id === URLKeys.memberNumber) {
                updatedFormData[URLKeys.memberVote] = 'all';
                updatedSearchParams.delete(URLKeys.memberVote);
                if (!value) {
                    updatedSearchParams.delete(URLKeys.memberNumber);
                }
            }
            if (id === URLKeys.sortBy && (value === 'nejnovejsi' || value === 'nejstarsi')) {
                setFilterFunc(() => sortingOptionsDict[value as 'nejnovejsi' | 'nejstarsi']);
            }
        };

        const handleSelectButtonChange = (id: string, value: any) => {
            updatedFormData = { ...formData, [id]: value };
            updateSearchParams(id, value.join(','), value.length > 0);
        };

        const updateSearchParams = (key: string, value: string, condition: boolean) => {
            if (condition) {
                if (key === URLKeys.sortBy && value === 'nejnovejsi') {
                    updatedSearchParams.delete(key);
                } else {
                    updatedSearchParams.set(key, value);
                }
            } else {
                updatedSearchParams.delete(key);
            }
        };

        if (id === URLKeys.show) {
            handleSelectButtonChange(id, value);
        } else {
            // voting related selectBtn defaults to 'all' if no value is selected
            if ((id === URLKeys.memberVote || id === URLKeys.voteResult) && value === null) {
                handleInputChange(id, 'all');
            } else {
                handleInputChange(id, value);
            }
        }

        // memberVote needs to be a number if > 2 because of difference between selectBtn and dropdown
        if (id === URLKeys.memberVote && value > 2) {
            updatedFormData[URLKeys.memberVote] = Number(value);
        } else if (id === URLKeys.memberVote && value !== null) {
            updatedFormData[URLKeys.memberVote] = value.toString();
        }

        // reset voteDescriptionText if fullText is selected and clicked again
        if (id === URLKeys.voteDescriptionText) {
            if (value === URLKeys.fullText && formData[URLKeys.voteDescriptionText] === URLKeys.fullText) {
                updatedFormData[URLKeys.voteDescriptionText] = null;
                updatedSearchParams.delete(URLKeys.voteDescriptionText);
            }
        }

        setFormData(updatedFormData);
        Object.entries(updatedFormData).forEach(([key, val]) => {
            if (val !== undefined && val !== 'all' && val !== '') {
                if (val !== null) {
                    updateSearchParams(key, val.toString(), true);
                }
            }
        });

        if (Array.isArray(updatedFormData[URLKeys.show])) {
            if (updatedFormData[URLKeys.show]?.length === 0) {
                updatedSearchParams.delete(URLKeys.show);
            }
        }
        // Preserve the page parameter unless explicitly reset
        if (id !== 'page') {
            updatedSearchParams.delete(URLKeys.page);
        }

        setSearchParams(updatedSearchParams, { preventScrollReset: true, replace: true });
    };

    const getDropdownOptions = async () => {
        try {
            const api = getApiClient();
            const councilId = parseInt(zastupitelstvoId ?? '0', 10);
            const [meetingData, memberData] = await Promise.all([
                api.getDropdownSessionOptionsByMunicipalityIdAndBodyAndCouncilId(municipalitaId, bodyType, councilId),
                api.getDropdownMemberOptionsByMunicipalityIdAndBodyAndCouncilId(municipalitaId, bodyType, councilId)
            ]);

            setDropdownOptions((prevOptions) => ({
                ...prevOptions,
                meeting: (meetingData ?? []).map((item) => ({ name: item.name, id: item.id })),
                member: (memberData ?? []).map((item) => ({ name: item.name, id: item.id }))
            }));

        } catch (error) {
            console.error(error);
        }
    };

    const getOtherVoteOptions = async () => {
        try {
            if (memberNumber == null) return;
            const api = getApiClient();
            const otherVoteOptionsData = await api.getDropdownOtherVoteOptionsByMunicipalityIdAndBodyAndMemberId(
                municipalitaId,
                bodyType,
                memberNumber
            );
            setDropdownOptions((prevOptions) => ({
                ...prevOptions,
                otherVoteOptions: (otherVoteOptionsData ?? []).map((item) => ({
                    id: item.id,
                    code: capitalizeFirstLetter(voteOptionLabels[item.id]?.label ?? item.code)
                }))
            }));
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        getDropdownOptions();
        if (sortBy) {
            if (typeof sortBy === 'string' && (sortBy === 'nejnovejsi' || sortBy === 'nejstarsi')) {
                setFilterFunc(() => sortingOptionsDict[sortBy]);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (memberNumber) {
            getOtherVoteOptions();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [memberNumber, voteOptionLabels]);

    useEffect(() => {
        setFormData({
            ...formData,
            [URLKeys.voteDescription]: searchParams.get(URLKeys.voteDescription) || '',
            [URLKeys.meetingNumber]: searchParams.get(URLKeys.meetingNumber)
                ? Number(searchParams.get(URLKeys.meetingNumber))
                : null,
            [URLKeys.memberNumber]: searchParams.get(URLKeys.memberNumber)
                ? Number(searchParams.get(URLKeys.memberNumber))
                : null,
            [URLKeys.memberVote]: (() => {
                const memberVote = searchParams.get(URLKeys.memberVote);
                return memberVote && Number(memberVote) > 2
                    ? Number(memberVote)
                    : (memberVote as VotingFormDataType[(typeof URLKeys)['memberVote']]) || 'all';
            })(),
            [URLKeys.voteResult]:
                (searchParams.get(URLKeys.voteResult) as VotingFormDataType[typeof URLKeys.voteResult]) || 'all',
            [URLKeys.show]: searchParams.get(URLKeys.show) ? searchParams.get(URLKeys.show)!.split(',') : [],
            [URLKeys.voteDescriptionText]: searchParams.get(URLKeys.voteDescriptionText)
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    const selectBoxOptionItem = (option: { label: string; value: string; icon?: string }) => {
        return (
            <>
                {option.icon && <span className='material-symbols-rounded'>{option.icon}</span>}
                <span className='p-button-label p-c'>{option.label}</span>
            </>
        );
    };

    return (
        <div className='votingDataCardsFilter'>
            <span className='p-float-label'>
                <InputText id={URLKeys.voteDescription} value={voteDescription ?? ''} onChange={onInputChange} />
                <label htmlFor={URLKeys.voteDescription}>{t('VotingDataCardsFilter.votingDescription')}</label>
            </span>
            <span className='p-float-label'>
                <Dropdown
                    id={URLKeys.meetingNumber}
                    value={meetingNumber}
                    onChange={onInputChange}
                    options={dropdownOptions.meeting}
                    optionValue='id'
                    optionLabel='name'
                    showClear
                />
                <label htmlFor={URLKeys.meetingNumber}>{t('VotingDataCardsFilter.meetingOne')}</label>
            </span>
            <span className='p-float-label'>
                <Dropdown
                    id={URLKeys.memberNumber}
                    value={memberNumber}
                    onChange={onInputChange}
                    options={dropdownOptions.member}
                    optionValue='id'
                    optionLabel='name'
                    showClear
                />
                <label htmlFor={URLKeys.memberNumber}>{t('selectMember', {ns: 'members'})}</label>
            </span>
            <div className='selectButton-group'>
                <span className='selectButton-wrapper'>
                    <label className='selectButton-label' htmlFor={URLKeys.show}>{t('VotingDataCardsFilter.alsoShow')}</label>
                    <SelectButton
                        id={URLKeys.show}
                        value={show}
                        itemTemplate={selectBoxOptionItem}
                        options={[
                            {
                                label: capitalizeFirstLetter(t('secretOption')),
                                value: URLKeys.VotingTypes.secretVotes
                            },
                            {
                                label: capitalizeFirstLetter(t('invalidOption')),
                                value: URLKeys.VotingTypes.invalidVotes
                            },
                            {
                                label: capitalizeFirstLetter(t('proceduralOption')),
                                value: URLKeys.VotingTypes.proceduralVotes
                            }
                        ]}
                        onChange={onInputChange}
                        multiple
                    />
                </span>
                <span className='selectButton-wrapper'>
                    <label className='selectButton-label' htmlFor={URLKeys.voteResult}>{t('VotingDataCardsFilter.votingResult')}</label>
                    <SelectButton
                        id={URLKeys.voteResult}
                        className='coloredOptions'
                        value={voteResult}
                        itemTemplate={selectBoxOptionItem}
                        options={[
                            { label: capitalizeFirstLetter(t('VotingDataCardsFilter.all')), value: 'all' },
                            { label: capitalizeFirstLetter(t('accepted')), value: '1', icon: 'check_box' },
                            { label: capitalizeFirstLetter(t('rejected')), value: '2', icon: 'disabled_by_default' }
                        ]}
                        onChange={onInputChange}
                    />
                </span>
                <span className={`selectButton-wrapper ${memberNumber ? 'fade-in' : 'fade-out'}`}>
                    {memberNumber && (
                        <>
                            <label className='selectButton-label' htmlFor={URLKeys.memberVote}>{capitalizeFirstLetter(t('votingOption'))}</label>
                            <SelectButton
                                id={URLKeys.memberVote}
                                className='coloredOptions'
                                value={typeof memberVote === 'number' && memberVote > 2 ? null : memberVote}
                                itemTemplate={selectBoxOptionItem}
                                options={[
                                    { label: t('VotingDataCardsFilter.all'), value: 'all' },
                                    { label: t('VotingDataCardsFilter.yes'), value: '1', icon: 'check_box' },
                                    {
                                        label: t('VotingDataCardsFilter.no'),
                                        value: '2',
                                        icon: 'disabled_by_default'
                                    }
                                ]}
                                onChange={onInputChange}
                            />
                        </>
                    )}
                </span>
            </div>
            <span className='p-float-label'>
                <Dropdown
                    id={URLKeys.sortBy}
                    value={sortBy}
                    onChange={onInputChange}
                    options={sortByOptions}
                />
                <label htmlFor={URLKeys.sortBy}>{t('sortBy', {ns: 'translation'})}</label>
            </span>
            <Button
                label={t('VotingDataCardsFilter.resetFilter')}
                className={`${searchParams.size === 0 ? 'fade-out' : 'fade-in'}`}
                icon='pi pi-refresh'
                onClick={() => {
                    const resetFormState = {
                        ...initialFormState,
                        [URLKeys.sortBy]: 'nejnovejsi' as 'nejnovejsi' | 'nejstarsi'
                    };
                    setFormData(resetFormState);
                    setSearchParams(new URLSearchParams(), { preventScrollReset: true, replace: true });
                    setFilterFunc(() => sortingOptionsDict['nejnovejsi']);
                }}
            />
            <span
                className={`p-float-label other-vote-options ${
                    memberNumber && dropdownOptions.otherVoteOptions.length > 0 ? 'fade-in' : 'fade-out'
                }`}
            >
                {memberNumber && (
                    <>
                        <Dropdown
                            id={URLKeys.memberVote}
                            value={typeof memberVote === 'number' && memberVote > 2 ? memberVote : null}
                            onChange={onInputChange}
                            options={dropdownOptions.otherVoteOptions}
                            optionValue='id'
                            optionLabel='code'
                        />
                        <label htmlFor={URLKeys.memberVote}>{t('VotingDataCardsFilter.otherVoteOptions')}</label>
                    </>
                )}
            </span>
            <div>
                <Checkbox
                    id={URLKeys.voteDescriptionText}
                    value={URLKeys.fullText}
                    onChange={onInputChange}
                    checked={formData[URLKeys.voteDescriptionText] === URLKeys.fullText || false}
                />
                <label htmlFor={URLKeys.voteDescriptionText} className='ml-2'>
                    {t('VotingDataCardsFilter.showFullDescription')}
                </label>
            </div>
        </div>
    );
};

export default VotingDataCardsFilter;
